import { Link, useParams } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/i18n";
import { LangSwitcher } from "@/components/LangSwitcher";
import { Wordmark } from "@/components/Nav";
import { LEGAL, LEGAL_VERSION } from "@/legal/texts";

export default function Legal() {
  const { doc } = useParams();
  const { config } = useAuth();
  const { t, lang } = useLang();
  const kind = doc === "terms" ? "terms" : "privacy";
  const text = LEGAL[kind][lang] || LEGAL[kind].ru;
  const fill = (s) => s.replaceAll("{brand}", config.brand);
  return (
    <div className="grain relative min-h-screen" data-testid={`legal-page-${kind}`}>
      <header className="relative z-10 mx-auto flex max-w-6xl items-center justify-between px-6 py-6">
        <Wordmark brand={config.brand} />
        <LangSwitcher compact />
      </header>
      <main className="relative z-10 mx-auto max-w-3xl px-6 pb-24 pt-6">
        <Link to="/" className="mb-8 inline-flex items-center gap-2 text-sm text-vox-dim hover:text-vox-text" data-testid="legal-back-link"><ArrowLeft size={14} />{t("common.back")}</Link>
        <div className="mb-8 flex gap-1 rounded-full border border-vox-line bg-vox-deep/60 p-1 w-fit">
          {["privacy", "terms"].map((k) => (
            <Link key={k} to={`/legal/${k}`} data-testid={`legal-tab-${k}`}
              className={`rounded-full px-4 py-1.5 text-xs transition-colors duration-200 ${kind === k ? "bg-vox-card text-vox-text" : "text-vox-dim hover:text-vox-text"}`}>
              {t(`legal.${k}`)}
            </Link>
          ))}
        </div>
        <div className="mono-label mb-3">{t("legal.version")} {LEGAL_VERSION}</div>
        <h1 className="text-3xl font-semibold tracking-tight text-vox-text sm:text-4xl" data-testid="legal-title">{fill(text.title)}</h1>
        <p className="mt-5 text-sm leading-relaxed text-vox-muted">{fill(text.intro)}</p>
        <div className="mt-10 space-y-8">
          {text.sections.map((s) => (
            <section key={s.h} className="border-t border-vox-line pt-6">
              <h2 className="text-base font-semibold text-vox-text md:text-lg">{s.h}</h2>
              {s.p.map((p, i) => <p key={i} className="mt-3 text-sm leading-relaxed text-vox-muted">{fill(p)}</p>)}
            </section>
          ))}
          <OperatorCard operator={config.operator} t={t} />
        </div>
      </main>
    </div>
  );
}

function OperatorCard({ operator, t }) {
  const rows = [
    ["operator_name", operator?.operator_name], ["operator_address", operator?.operator_address], ["operator_email", operator?.operator_email],
    ["operator_phone", operator?.operator_phone], ["dpo_name", operator?.dpo_name], ["dpo_email", operator?.dpo_email],
  ].filter(([, v]) => v);
  return (
    <section className="panel border-vox-accent/30 p-6" data-testid="legal-operator-card">
      <h2 className="text-base font-semibold text-vox-text md:text-lg">{t("legal.contacts")}</h2>
      {rows.length === 0 ? <p className="mt-3 text-sm text-vox-dim" data-testid="legal-operator-empty">{t("legal.contactsEmpty")}</p> : (
        <dl className="mt-4 grid gap-2 text-sm sm:grid-cols-[220px_1fr]">
          {rows.map(([k, v]) => [<dt key={`${k}-k`} className="font-mono text-[11px] uppercase tracking-wider text-vox-dim">{t(`admin.${k}`)}</dt>, <dd key={`${k}-v`} className="text-vox-text" data-testid={`legal-${k.replaceAll("_", "-")}`}>{v}</dd>])}
        </dl>
      )}
    </section>
  );
}
